import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Eye, Check, AlertTriangle, FileText, Calculator, Users, Save } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TextArea } from '../TextArea';

interface ProvaFinalizada {
  id: string;
  id_militar: string;
  militar: {
    nome_guerra: string;
    patente: string;
  };
  status: string;
  pontuacao_obtida: number | null;
  total_questoes: number;
  questoes_respondidas: number;
  questoes_corrigidas: number;
  acertos: number;
  erros: number;
  parciais: number;
  data_finalizacao: Date;
  tempo_gasto_minutos: number;
  resultado_divulgado: boolean;
  prova: {
    id: string;
    titulo: string;
    pontuacao_maxima: number;
  };
}

interface DivulgarResultadosModalProps {
  isOpen: boolean;
  onClose: () => void;
  prova: {
    id: string;
    titulo: string;
    total_provas_finalizadas: number;
  };
  onResultadoDivulgado: () => void;
}

export const DivulgarResultadosModal: React.FC<DivulgarResultadosModalProps> = ({
  isOpen,
  onClose,
  prova,
  onResultadoDivulgado
}) => {
  const [provasFinalizadas, setProvasFinalizadas] = useState<ProvaFinalizada[]>([]);
  const [provaAtual, setProvaAtual] = useState(0);
  const [loading, setLoading] = useState(false);
  const [divulgandoResultado, setDivulgandoResultado] = useState(false);
  const [visualizandoQuestoes, setVisualizandoQuestoes] = useState(false);
  const [resultados, setResultados] = useState<any[]>([]);
  const [processandoCorrecao, setProcessandoCorrecao] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [correcoes, setCorrecoes] = useState<{
    [id_resultado: string]: {
      pontuacao_obtida: number;
      status_correcao: string;
      comentario_correcao: string;
    };
  }>({});

  useEffect(() => {
    if (isOpen) {
      carregarProvasFinalizadas();
      setVisualizandoQuestoes(false);
    }
  }, [isOpen, prova.id]);

  const carregarProvasFinalizadas = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/provas/${prova.id}/resultados-finalizados`);
      setProvasFinalizadas(response.data);
      setProvaAtual(0);
    } catch (error: any) {
      console.error('Erro ao carregar provas finalizadas:', error);
      enqueueSnackbar('Erro ao carregar provas finalizadas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const carregarQuestoes = async (acessoId: string) => {
    try {
      setLoading(true);

      // Carregar resultados para correção
      const resultadosResponse = await api.get(`/provas/acessos/${acessoId}/resultados`);

      // Se não há resultados, tentar inicializar ou processar correção
      if (resultadosResponse.data.length === 0) {
        // Verificar se há respostas
        try {
          const respostasResponse = await api.get(`/provas/acessos/${acessoId}/respostas-correcao`);

          if (respostasResponse.data.length > 0) {
            // Há respostas, processar correção automática
            await api.post(`/provas/acessos/${acessoId}/correcao-automatica`, {
              processar_multipla_escolha: true,
              processar_texto_livre: false
            });

            // Recarregar resultados
            const novoResultadosResponse = await api.get(`/provas/acessos/${acessoId}/resultados`);
            setResultados(novoResultadosResponse.data);

            // Inicializar correções
            const correcoesIniciais: any = {};
            novoResultadosResponse.data.forEach((resultado: any) => {
              correcoesIniciais[resultado.id] = {
                pontuacao_obtida: resultado.pontuacao_obtida || 0,
                status_correcao: resultado.status_correcao || 'pendente',
                comentario_correcao: resultado.comentario_correcao || ''
              };
            });
            setCorrecoes(correcoesIniciais);

            enqueueSnackbar('Correção automática processada!', { variant: 'success' });
          } else {
            // Sem respostas, inicializar resultados vazios
            await api.post(`/provas/acessos/${acessoId}/inicializar-resultados`);

            const novoResultadosResponse = await api.get(`/provas/acessos/${acessoId}/resultados`);
            setResultados(novoResultadosResponse.data);

            const correcoesIniciais: any = {};
            novoResultadosResponse.data.forEach((resultado: any) => {
              correcoesIniciais[resultado.id] = {
                pontuacao_obtida: resultado.pontuacao_obtida || 0,
                status_correcao: resultado.status_correcao || 'pendente',
                comentario_correcao: resultado.comentario_correcao || ''
              };
            });
            setCorrecoes(correcoesIniciais);

            enqueueSnackbar('Resultados inicializados para correção manual', { variant: 'info' });
          }
        } catch (error) {
          console.error('Erro ao processar correção:', error);
          throw error;
        }
      } else {
        setResultados(resultadosResponse.data);

        // Inicializar correções
        const correcoesIniciais: any = {};
        resultadosResponse.data.forEach((resultado: any) => {
          correcoesIniciais[resultado.id] = {
            pontuacao_obtida: resultado.pontuacao_obtida || 0,
            status_correcao: resultado.status_correcao || 'pendente',
            comentario_correcao: resultado.comentario_correcao || ''
          };
        });
        setCorrecoes(correcoesIniciais);
      }

      setVisualizandoQuestoes(true);
    } catch (error: any) {
      console.error('Erro ao carregar questões:', error);
      enqueueSnackbar('Erro ao carregar questões da prova', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const navegarProva = (direcao: 'anterior' | 'proxima') => {
    if (direcao === 'anterior' && provaAtual > 0) {
      setProvaAtual(provaAtual - 1);
      setVisualizandoQuestoes(false);
    } else if (direcao === 'proxima' && provaAtual < provasFinalizadas.length - 1) {
      setProvaAtual(provaAtual + 1);
      setVisualizandoQuestoes(false);
    }
  };

  const handleCorrecaoChange = (
    id_resultado: string,
    field: 'pontuacao_obtida' | 'status_correcao' | 'comentario_correcao',
    value: any
  ) => {
    setCorrecoes(prev => ({
      ...prev,
      [id_resultado]: {
        ...prev[id_resultado],
        [field]: value
      }
    }));
  };

  const salvarCorrecao = async (resultado: any) => {
    try {
      setSalvando(true);

      const correcao = correcoes[resultado.id] || {
        pontuacao_obtida: resultado.pontuacao_obtida,
        status_correcao: resultado.status_correcao,
        comentario_correcao: resultado.comentario_correcao || ''
      };

      await api.post('/provas/correcao-manual', {
        id_resultado: resultado.id,
        pontuacao_obtida: Number(correcao.pontuacao_obtida),
        status_correcao: correcao.status_correcao,
        comentario_correcao: correcao.comentario_correcao || null
      });

      // Atualizar resultado local
      setResultados(prev => prev.map(r =>
        r.id === resultado.id
          ? {
              ...r,
              pontuacao_obtida: Number(correcao.pontuacao_obtida),
              status_correcao: correcao.status_correcao,
              comentario_correcao: correcao.comentario_correcao
            }
          : r
      ));

      enqueueSnackbar('Correção salva com sucesso!', { variant: 'success' });

      // Recarregar lista de provas finalizadas para atualizar contadores
      await carregarProvasFinalizadas();
    } catch (error: any) {
      console.error('Erro ao salvar correção:', error);
      enqueueSnackbar('Erro ao salvar correção', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const salvarTodasCorrecoes = async () => {
    try {
      setSalvando(true);

      for (const resultado of resultados) {
        const correcao = correcoes[resultado.id] || {
          pontuacao_obtida: resultado.pontuacao_obtida,
          status_correcao: resultado.status_correcao,
          comentario_correcao: resultado.comentario_correcao || ''
        };

        await api.post('/provas/correcao-manual', {
          id_resultado: resultado.id,
          pontuacao_obtida: Number(correcao.pontuacao_obtida),
          status_correcao: correcao.status_correcao,
          comentario_correcao: correcao.comentario_correcao || null
        });
      }

      setResultados(prev => prev.map(r => {
        const correcao = correcoes[r.id];
        if (correcao) {
          return {
            ...r,
            pontuacao_obtida: Number(correcao.pontuacao_obtida),
            status_correcao: correcao.status_correcao,
            comentario_correcao: correcao.comentario_correcao
          };
        }
        return r;
      }));

      setCorrecoes({});

      enqueueSnackbar('Todas as correções foram salvas!', { variant: 'success' });

      await carregarProvasFinalizadas();
    } catch (error: any) {
      console.error('Erro ao salvar correções:', error);
      enqueueSnackbar('Erro ao salvar correções', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const divulgarResultado = async (acessoId: string) => {
    try {
      setDivulgandoResultado(true);
      await api.put(`/provas/acessos/${acessoId}/divulgar-resultado`);

      // Atualizar a prova atual na lista
      setProvasFinalizadas(prev => prev.map(p =>
        p.id === acessoId ? { ...p, resultado_divulgado: true } : p
      ));

      enqueueSnackbar('Resultado divulgado com sucesso!', { variant: 'success' });
    } catch (error: any) {
      console.error('Erro ao divulgar resultado:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao divulgar resultado',
        { variant: 'error' }
      );
    } finally {
      setDivulgandoResultado(false);
    }
  };

  const divulgarTodosResultados = async () => {
    try {
      setDivulgandoResultado(true);

      // Filtrar apenas provas que não foram divulgadas E estão totalmente corrigidas
      const provasNaoDivulgadas = provasFinalizadas.filter(
        p => !p.resultado_divulgado && p.questoes_corrigidas === p.total_questoes
      );

      if (provasNaoDivulgadas.length === 0) {
        enqueueSnackbar('Não há provas prontas para divulgação', { variant: 'warning' });
        return;
      }

      const provasPendenteCorrecao = provasFinalizadas.filter(
        p => !p.resultado_divulgado && p.questoes_corrigidas < p.total_questoes
      );

      for (const prova of provasNaoDivulgadas) {
        await api.put(`/provas/acessos/${prova.id}/divulgar-resultado`);
      }

      // Atualizar as provas divulgadas
      setProvasFinalizadas(prev => prev.map(p =>
        provasNaoDivulgadas.find(pd => pd.id === p.id)
          ? { ...p, resultado_divulgado: true }
          : p
      ));

      let mensagem = `${provasNaoDivulgadas.length} resultado(s) divulgado(s) com sucesso!`;
      if (provasPendenteCorrecao.length > 0) {
        mensagem += ` ${provasPendenteCorrecao.length} prova(s) aguardando correção manual.`;
      }

      enqueueSnackbar(mensagem, {
        variant: provasPendenteCorrecao.length > 0 ? 'warning' : 'success'
      });

      onResultadoDivulgado();
    } catch (error: any) {
      console.error('Erro ao divulgar todos os resultados:', error);
      enqueueSnackbar('Erro ao divulgar resultados em lote', { variant: 'error' });
    } finally {
      setDivulgandoResultado(false);
    }
  };

  const calcularPorcentagem = (obtida: number, maxima: number): number => {
    return maxima > 0 ? Math.round((obtida / maxima) * 100) : 0;
  };

  const getStatusCorrecao = (prova: ProvaFinalizada): {
    status: 'completa' | 'parcial' | 'pendente';
    texto: string;
    cor: string;
  } => {
    if (prova.questoes_corrigidas === prova.total_questoes) {
      return {
        status: 'completa',
        texto: 'Correção Completa',
        cor: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'
      };
    } else if (prova.questoes_corrigidas > 0) {
      return {
        status: 'parcial',
        texto: `${prova.questoes_corrigidas}/${prova.total_questoes} Corrigidas`,
        cor: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700'
      };
    } else {
      return {
        status: 'pendente',
        texto: 'Correção Pendente',
        cor: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
      };
    }
  };

  if (!isOpen) return null;

  const provaAtualData = provasFinalizadas[provaAtual];
  const totalProvas = provasFinalizadas.length;
  const provasDivulgadas = provasFinalizadas.filter(p => p.resultado_divulgado).length;
  const provasProntasParaDivulgar = provasFinalizadas.filter(
    p => !p.resultado_divulgado && p.questoes_corrigidas === p.total_questoes
  ).length;
  const provasPendentesCorrecao = provasFinalizadas.filter(
    p => !p.resultado_divulgado && p.questoes_corrigidas < p.total_questoes
  ).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold">Divulgar Resultados</h2>
            <p className="text-sm text-muted-foreground">{prova.titulo}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Carregando provas finalizadas...</p>
          </div>
        ) : totalProvas === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium text-foreground">Nenhuma prova finalizada</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Ainda não há provas finalizadas para divulgar resultados.
            </p>
          </div>
        ) : (
          <>
            {/* Estatísticas gerais */}
            <div className="p-6 border-b border-border bg-muted/30">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-sm font-medium">Total de Provas</p>
                        <p className="text-2xl font-bold text-blue-600">{totalProvas}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <Check className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-sm font-medium">Divulgadas</p>
                        <p className="text-2xl font-bold text-green-600">{provasDivulgadas}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      <div>
                        <p className="text-sm font-medium">Prontas</p>
                        <p className="text-2xl font-bold text-orange-600">{provasProntasParaDivulgar}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm font-medium">Aguardando Correção</p>
                        <p className="text-2xl font-bold text-red-600">{provasPendentesCorrecao}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {provasProntasParaDivulgar > 0 && (
                <div className="mt-4 space-y-2">
                  <Button
                    onClick={divulgarTodosResultados}
                    disabled={divulgandoResultado}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {divulgandoResultado ? 'Divulgando...' : `Divulgar Prontas (${provasProntasParaDivulgar})`}
                  </Button>
                  {provasPendentesCorrecao > 0 && (
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                      <AlertTriangle className="inline h-4 w-4 mr-1" />
                      {provasPendentesCorrecao} prova(s) aguardando correção manual de questões dissertativas
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Navegação e detalhes da prova atual */}
            {provaAtualData && (
              <div className="p-6">
                {/* Controles de navegação */}
                <div className="flex items-center justify-between mb-6">
                  <Button
                    variant="outline"
                    onClick={() => navegarProva('anterior')}
                    disabled={provaAtual === 0}
                    className="flex items-center space-x-2"
                  >
                    <ChevronLeft size={16} />
                    <span>Anterior</span>
                  </Button>

                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Prova {provaAtual + 1} de {totalProvas}
                    </p>
                    <div className="flex space-x-1 mt-1">
                      {provasFinalizadas.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full ${
                            index === provaAtual
                              ? 'bg-blue-500'
                              : provasFinalizadas[index].resultado_divulgado
                              ? 'bg-green-400'
                              : 'bg-muted-foreground/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navegarProva('proxima')}
                    disabled={provaAtual === totalProvas - 1}
                    className="flex items-center space-x-2"
                  >
                    <span>Próxima</span>
                    <ChevronRight size={16} />
                  </Button>
                </div>

                {/* Detalhes da prova atual */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <span>{provaAtualData.militar.patente} {provaAtualData.militar.nome_guerra}</span>
                          {provaAtualData.resultado_divulgado && (
                            <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700">
                              Resultado Divulgado
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Finalizada em {new Date(provaAtualData.data_finalizacao).toLocaleString('pt-BR')} •
                          Tempo: {provaAtualData.tempo_gasto_minutos} minutos
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Status da correção */}
                      <div>
                        <h4 className="font-medium mb-3">Status da Correção</h4>
                        <div className="space-y-3">
                          <Badge className={getStatusCorrecao(provaAtualData).cor}>
                            {getStatusCorrecao(provaAtualData).texto}
                          </Badge>

                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>Total de questões: {provaAtualData.total_questoes}</p>
                            <p>Questões respondidas: {provaAtualData.questoes_respondidas}</p>
                            <p>Questões corrigidas: {provaAtualData.questoes_corrigidas}</p>
                          </div>

                          {/* Acertos e Erros */}
                          {provaAtualData.questoes_corrigidas > 0 && (
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                                <p className="text-lg font-bold text-green-600">{provaAtualData.acertos || 0}</p>
                                <p className="text-xs text-green-700 dark:text-green-400">Acertos</p>
                              </div>
                              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                                <p className="text-lg font-bold text-red-600">{provaAtualData.erros || 0}</p>
                                <p className="text-xs text-red-700 dark:text-red-400">Erros</p>
                              </div>
                              <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                                <p className="text-lg font-bold text-yellow-600">{provaAtualData.parciais || 0}</p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-400">Parciais</p>
                              </div>
                            </div>
                          )}

                          {provaAtualData.questoes_corrigidas < provaAtualData.total_questoes && (
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                              <div className="flex items-start space-x-2">
                                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                                <div className="text-sm">
                                  <p className="font-medium text-yellow-800 dark:text-yellow-300">Atenção</p>
                                  <p className="text-yellow-700 dark:text-yellow-400">
                                    Ainda há questões pendentes de correção manual.
                                    Acesse o sistema de correção para finalizar.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pontuação */}
                      <div>
                        <h4 className="font-medium mb-3">Pontuação</h4>
                        <div className="space-y-3">
                          {provaAtualData.pontuacao_obtida !== null ? (
                            <>
                              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <p className="text-3xl font-bold text-blue-600">
                                  {provaAtualData.pontuacao_obtida}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  de {provaAtualData.prova.pontuacao_maxima} pontos
                                </p>
                                <p className="text-lg font-medium text-blue-700">
                                  {calcularPorcentagem(provaAtualData.pontuacao_obtida, provaAtualData.prova.pontuacao_maxima)}%
                                </p>
                              </div>
                            </>
                          ) : (
                            <div className="text-center p-4 bg-muted rounded-lg">
                              <p className="text-muted-foreground">Pontuação não calculada</p>
                              <p className="text-sm text-muted-foreground/70">
                                Aguardando correção das questões
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="mt-6 pt-4 border-t border-border flex justify-between">
                      <Button
                        variant="outline"
                        onClick={() => carregarQuestoes(provaAtualData.id)}
                        className="flex items-center space-x-2"
                      >
                        <Eye size={16} />
                        <span>Ver Correção</span>
                      </Button>

                      {!provaAtualData.resultado_divulgado &&
                       provaAtualData.questoes_corrigidas === provaAtualData.total_questoes && (
                        <Button
                          onClick={() => divulgarResultado(provaAtualData.id)}
                          disabled={divulgandoResultado}
                          className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2"
                        >
                          <Check size={16} />
                          <span>
                            {divulgandoResultado ? 'Divulgando...' : 'Divulgar Resultado'}
                          </span>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Visualização das Questões */}
                {visualizandoQuestoes && (
                  <Card className="mt-4">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Questões e Correções</CardTitle>
                        <div className="flex items-center gap-2">
                          {!provaAtualData.resultado_divulgado && (
                            <Button
                              size="sm"
                              onClick={salvarTodasCorrecoes}
                              disabled={salvando}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              <Save size={14} className="mr-1" />
                              {salvando ? 'Salvando...' : 'Salvar Todas as Correções'}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setVisualizandoQuestoes(false)}
                          >
                            Fechar
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {resultados.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Nenhuma questão encontrada</p>
                      ) : (
                        <div className="space-y-4">
                          {resultados.map((resultado, index) => {
                            const correcaoAtual = correcoes[resultado.id] || {
                              pontuacao_obtida: resultado.pontuacao_obtida || 0,
                              status_correcao: resultado.status_correcao || 'pendente',
                              comentario_correcao: resultado.comentario_correcao || ''
                            };

                            return (
                              <div key={resultado.id} className="border border-border rounded-lg p-4 bg-muted/30">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-foreground">
                                      Questão {index + 1}
                                      {resultado.questao_titulo && ` - ${resultado.questao_titulo}`}
                                    </h4>
                                    <p className="text-sm text-foreground/80 mt-1 whitespace-pre-wrap">{resultado.questao_enunciado}</p>
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Tipo: {resultado.questao_tipo === 'multipla_escolha' ? 'Múltipla Escolha' : 'Texto Livre'}
                                      {' | '}
                                      Pontuação: {resultado.questao_pontuacao}
                                    </p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-3 text-sm bg-background p-3 rounded border border-border">
                                  <div>
                                    <span className="text-muted-foreground font-medium">Resposta Dada:</span>
                                    <p className="mt-1">{resultado.resposta_dada || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground font-medium">Resposta Correta:</span>
                                    <p className="text-green-600 mt-1">{resultado.resposta_correta || '-'}</p>
                                  </div>
                                </div>

                                {/* Campos de Correção */}
                                {!provaAtualData.resultado_divulgado ? (
                                  <div className="mt-4 space-y-3 bg-background p-4 rounded border border-border">
                                    <h5 className="font-medium text-foreground mb-3">Correção</h5>

                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                          Status da Correção
                                        </label>
                                        <select
                                          value={correcaoAtual.status_correcao}
                                          onChange={(e) => handleCorrecaoChange(resultado.id, 'status_correcao', e.target.value)}
                                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                        >
                                          <option value="pendente">Pendente</option>
                                          <option value="correta">Correta</option>
                                          <option value="incorreta">Incorreta</option>
                                          <option value="parcial">Parcial</option>
                                        </select>
                                      </div>

                                      <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                          Pontuação Obtida (max: {resultado.questao_pontuacao})
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          max={resultado.questao_pontuacao}
                                          step="0.1"
                                          value={correcaoAtual.pontuacao_obtida}
                                          onChange={(e) => handleCorrecaoChange(resultado.id, 'pontuacao_obtida', parseFloat(e.target.value) || 0)}
                                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                        />
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-foreground mb-1">
                                        Comentário da Correção (opcional)
                                      </label>
                                      <textarea
                                        value={correcaoAtual.comentario_correcao}
                                        onChange={(e) => handleCorrecaoChange(resultado.id, 'comentario_correcao', e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                        placeholder="Adicione um comentário sobre a correção..."
                                      />
                                    </div>


                                  </div>
                                ) : (
                                  <div className="mt-4 space-y-3 bg-muted/30 p-4 rounded border border-border">
                                    <div className="flex items-center justify-between mb-3">
                                      <h5 className="font-medium text-foreground">Correção</h5>
                                      <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700">
                                        Resultado Divulgado
                                      </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-muted-foreground font-medium">Status:</span>
                                        <p className="mt-1 font-medium capitalize">
                                          {correcaoAtual.status_correcao === 'correta' && (
                                            <span className="text-green-600">✓ Correta</span>
                                          )}
                                          {correcaoAtual.status_correcao === 'incorreta' && (
                                            <span className="text-red-600">✗ Incorreta</span>
                                          )}
                                          {correcaoAtual.status_correcao === 'parcial' && (
                                            <span className="text-yellow-600">◐ Parcial</span>
                                          )}
                                          {correcaoAtual.status_correcao === 'pendente' && (
                                            <span className="text-muted-foreground">⊙ Pendente</span>
                                          )}
                                        </p>
                                      </div>

                                      <div>
                                        <span className="text-muted-foreground font-medium">Pontuação Obtida:</span>
                                        <p className="mt-1 font-medium">
                                          {correcaoAtual.pontuacao_obtida} / {resultado.questao_pontuacao}
                                        </p>
                                      </div>
                                    </div>

                                    {correcaoAtual.comentario_correcao && (
                                      <div className="mt-3 p-3 bg-background rounded border border-border text-sm">
                                        <span className="text-foreground font-medium">Comentário:</span>
                                        <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{correcaoAtual.comentario_correcao}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
